import React, { forwardRef } from 'react'

/**
 * The single Button primitive used across the app.
 *
 * Variants and sizes are defined in `docs/contributor/design-language.md` §6.1 and
 * implemented as CSS classes in `src/ui/styles/ui.css`.
 *
 * Loading state replaces the label with `loadingLabel` (typically the
 * present-progressive form, e.g. "Saving…") and disables the button.
 * No spinner — see design doc §8.2.
 *
 * `active` adds a highlighted state (used by the Tasks page "Filters"
 * trigger when one or more filters are applied). `count` renders a small
 * count badge after the label.
 */
export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    loadingLabel,
    active = false,
    count,
    disabled = false,
    type = 'button',
    className = '',
    children,
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading
  const classes = [
    'btn',
    `btn--${variant}`,
    `btn--${size}`,
    active ? 'btn--active' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ')
  const showCount = typeof count === 'number' && count > 0 && !loading
  return (
    <button
      ref={ref}
      type={type}
      className={classes}
      disabled={isDisabled}
      data-variant={variant}
      data-size={size}
      data-loading={loading || undefined}
      data-active={active || undefined}
      {...rest}
    >
      {loading && loadingLabel ? loadingLabel : children}
      {showCount && <span className="btn__count">{count}</span>}
    </button>
  )
})
